import axios from 'axios';
import { getServiceErrorMessage } from './utilities';
import { isExpired } from '../utilities/dateTimes';
import { configService } from './configService';

export const PROVIDERKEY = 'cw.pt';
export const IDENTITYKEY = 'cw.idt';
export const REFRESHKEY = 'cw.ret';
export const ACCESSINFOKEY = 'cw.act';


/* PROVIDER */

/**
 * Gets a cached provider token if one exists and is not expired, unless skipExpiryCheck is true
 * 
 * @param {boolean} skipExpiryCheck - if true, the cached provider token will be returned even if it is expired
 * @returns {object|null} - the cached provider token, or null if there is no cached provider token or it is expired
 */
export const getCachedProviderToken = (skipExpiryCheck) => {
    const cachedProvider = JSON.parse(localStorage.getItem(PROVIDERKEY));

    if (cachedProvider) {
        const expiry = new Date(cachedProvider.expiry);

        if (skipExpiryCheck || !isExpired(expiry)) {
            return cachedProvider;
        }
    }

    return null;
}

/**
 * Caches a provider token and returns it if successful
 * 
 * @param {object} providerToken - the provider token to cache
 * @returns {object} - the cached provider token
 */
export const setCachedProviderToken = (providerToken) => {
    localStorage.setItem(PROVIDERKEY, JSON.stringify(providerToken));

    return getCachedProviderToken();
}


/* IDENTITY */

/**
 * Gets a cached identity token if one exists and is not expired, unless skipExpiryCheck is true
 * 
 * @param {boolean} skipExpiryCheck - if true, the cached identity token will be returned even if it is expired
 * @returns {object} - the cached identity token, or null if there is no cached identity token or it is expired
 */
export const getCachedIdentityToken = (skipExpiryCheck) => {
    const cachedIdentity = JSON.parse(localStorage.getItem(IDENTITYKEY));

    if (cachedIdentity) {
        const expiry = new Date(cachedIdentity.idTokenExpires?.seconds * 1000);
        
        if (skipExpiryCheck || !isExpired(expiry)) {
            return cachedIdentity;
        }
    }

    return null;
}

/**
 * Gets an identity token from the identity service
 * 
 * @param {number} providerType - the type of provider (e.g. Microsoft or Google) as an enumerated integer
 * @param {GUID} providerTenantId - the tenant ID for the provider
 * @param {object} providerIdToken - the ID token from the provider
 * @param {object} providerAccessToken - the access token from the provider
 * @returns {object|error} - the identity token
 */
export const getIdentityToken = async (providerType, providerTenantId, providerIdToken, providerAccessToken) => {
    const url = `${configService.adminGatewayUrl}/identity/id/token`;
    const postData = {
        providerType,
        providerTenantId,
        providerIdToken,
        providerAccessToken
    }
    const response = await axios.post(url, postData);

    if (response.data?.status !== 200 || !response.data?.identity) {
        throw getServiceErrorMessage(response);
    }

    localStorage.setItem(IDENTITYKEY, JSON.stringify(response.data.identity));
    return response.data.identity;
}


/* REFRESH TOKEN */

/**
 * Gets a cached refresh token if one exists and is not expired, unless skipExpiryCheck is true
 * 
 * @param {boolean} skipExpiryCheck - if true, the cached access token will be returned even if it is expired
 * @returns {object} - the cached access token, or null if there is no cached access token or it is expired
 */
export const getCachedRefreshToken = (skipExpiryCheck) => {
    const cachedRefreshToken = JSON.parse(localStorage.getItem(REFRESHKEY));

    if (cachedRefreshToken) {
        const expiry = new Date(cachedRefreshToken.refreshTokenExpires);

        if (skipExpiryCheck || !isExpired(expiry)) {
            return cachedRefreshToken;
        }
    }

    return null;
}

/**
 * Gets refresh AND access token from the identity service
 * 
 * @param {object} idToken - the ID token from the identity service
 * @param {string} tenantId - the tenant ID for the application
 * @returns {object|error} - the access token or an error, if one occurred    
 */
export const getRefreshToken = async (idToken, tenantId) => {
    const url = `${configService.adminGatewayUrl}/identity/refresh/token`;
    const postData = {
        idToken,
        tenantId
    };

    const headers = {
        Authorization: `Bearer ${idToken}`
    }

    const response = await axios.post(url, postData, {headers});

    if (response.data?.status !== 200 || !response.data?.access) {
        throw getServiceErrorMessage(response);
    }

    localStorage.setItem(REFRESHKEY, JSON.stringify(response.data.access));
    localStorage.setItem(ACCESSINFOKEY, JSON.stringify(response.data.access));
    return response.data.access;
}


/* ACCESS TOKEN */

/**
 * Gets a cached access token if one exists and is not expired, unless skipExpiryCheck is true
 * 
 * @param {boolean} skipExpiryCheck - if true, the cached access token will be returned even if it is expired
 * @returns {object} - the cached access token, or null if there is no cached access token or it is expired
 */
export const getCachedAccessToken = (skipExpiryCheck) => {
    const cachedAccessToken = JSON.parse(localStorage.getItem(ACCESSINFOKEY));

    if (cachedAccessToken) {
        const expiry = new Date(cachedAccessToken.accessTokenExpires);

        if (skipExpiryCheck || !isExpired(expiry)) {
            return cachedAccessToken;
        }
    }

    return null;
}

/**
 * Gets an access token from the identity service using a refresh token
 * 
 * @param {object} refreshToken- the refresh token from the identity service
 * @param {string} tenantId - the tenant ID for the application
 * @returns {object|error} - the access token or an error, if one occurred    
 */
export const getAccessToken = async (refreshToken, tenantId) => {
    const url = `${configService.adminGatewayUrl}/identity/access/token`;
    const postData = {
        refreshToken,
        tenantId
    };

    const headers = {
        Authorization: `Bearer ${refreshToken}`
    }

    const response = await axios.post(url, postData, { headers });

    if (response.data?.status !== 200 || !response.data?.access) {
        throw getServiceErrorMessage(response);
    }

    localStorage.setItem(REFRESHKEY, JSON.stringify(response.data.access));
    localStorage.setItem(ACCESSINFOKEY, JSON.stringify(response.data.access));

    return response.data.access;
}

/**
 * Gets an identity token and access token from a provider token
 * 
 * @param {GUID} appId - the ID of the application for a provider
 * @param {number} authProvider - the type of provider (e.g. Microsoft or Google) as an enumerated integer
 * @param {object} providerToken - the token from the provider
 * @returns {object} - the identity token and access token
 */                      
export const getTokensFromProviderToken = async (appId, authProvider, providerToken) => {
    if (typeof providerToken === 'object') {
        const identityToken = await getIdentityToken(authProvider, providerToken.tenantId, providerToken.idToken, providerToken.accessToken);

        if (identityToken) {
            const accessToken = await getRefreshToken(identityToken.idToken, appId);

            return {
                identityToken,
                accessToken
            };
        }
    }

    return null;
}
